import { InputProps, SelectProps, TextAreaProps } from '@heroui/react';

/* ============================================================================
   Standardowe presety pól formularzy (heroui) — JEDNO źródło prawdy.

   Standard: bordered, wysokość 50px, label-outside, akcent fokusu #E07B2D.
   Spread w widokach: `<Input {...inputProps} />`, `<Select {...selectProps} />`,
   `<Textarea {...textareaProps} />`. Wariant kompaktowy (38px) — patrz
   `compactFormProps.ts`.

   UWAGA (Tailwind JIT): klasy muszą występować jako literały, więc akcent
   fokusu #E07B2D jest wpisany wprost we fragmentach poniżej (nie interpolujemy
   koloru do `border-[...]`, bo JIT by go nie wygenerował). Fragmenty są
   współdzielone, żeby uniknąć rozjazdu (jak teal-duplikat / radius w przeszłości).
   ========================================================================= */

/** Powierzchnia pola: tło, ramka 1px, zaokrąglenie, brak cienia, dark-mode.
 *  Eksportowane — współdzielone też przez kompaktowe presety (`compactFormProps.ts`). */
export const FIELD_SURFACE = 'bg-white dark:bg-[#242a33] border-1 border-gray-300 dark:border-[#374151] '
  + 'rounded-md shadow-none transition-colors';

/** Fokus (#E07B2D) + hover dla Input/Textarea (wrapper w grupie → group-data). */
export const FIELD_FOCUS_HOVER = 'group-data-[focus=true]:border-[#E07B2D] dark:group-data-[focus=true]:border-[#E07B2D] '
  + 'group-data-[hover=true]:border-gray-400 dark:group-data-[hover=true]:border-[#4b5563]';

/** Fokus (#E07B2D) + hover dla triggera Selecta (data-* zamiast group-data-*). */
/** Dropdown: hover i otwarcie zmieniają tylko tło, ramka zostaje bazowa, bez outline/ring. */
export const SELECT_FOCUS_HOVER = 'outline-none ring-0 data-[focus-visible=true]:ring-0 data-[focus-visible=true]:outline-none '
  + 'data-[hover=true]:border-gray-300 dark:data-[hover=true]:border-[#374151] '
  + 'data-[focus=true]:border-gray-300 dark:data-[focus=true]:border-[#374151] '
  + 'data-[open=true]:border-gray-300 dark:data-[open=true]:border-[#374151] '
  + 'data-[hover=true]:bg-gray-50 dark:data-[hover=true]:bg-[#2a313b] '
  + 'data-[open=true]:bg-gray-50 dark:data-[open=true]:bg-[#2a313b]';

/** Tekst pola + placeholder (Input/Textarea). */
const FIELD_TEXT = 'text-sm placeholder:text-gray-400 dark:placeholder:text-gray-500 dark:text-gray-100';

/** Wspólne sloty etykiet/opisu/błędu dla wszystkich pól. */
const FIELD_LABELS = {
  label: 'dark:text-gray-300',
  description: 'dark:text-gray-400',
  errorMessage: 'dark:text-red-400',
};

export const inputProps: InputProps = {
  size: 'lg',
  radius: 'sm',
  variant: 'bordered',
  labelPlacement: 'outside',
  classNames: {
    input: FIELD_TEXT,
    inputWrapper: `h-[50px] ${FIELD_SURFACE} ${FIELD_FOCUS_HOVER}`,
    ...FIELD_LABELS,
  },
};

export const textareaProps: TextAreaProps = {
  size: 'lg',
  radius: 'sm',
  variant: 'bordered',
  labelPlacement: 'outside',
  classNames: {
    input: FIELD_TEXT,
    inputWrapper: `${FIELD_SURFACE} ${FIELD_FOCUS_HOVER}`,
    ...FIELD_LABELS,
  },
};

export const selectProps: Partial<SelectProps> = {
  size: 'lg',
  radius: 'sm',
  variant: 'bordered',
  labelPlacement: 'outside',
  classNames: {
    base: 'w-full',
    trigger: `h-[50px] ${FIELD_SURFACE} ${SELECT_FOCUS_HOVER}`,
    value: 'text-sm font-normal text-gray-700 dark:text-gray-100',
    label: FIELD_LABELS.label,
    popoverContent: 'rounded-md dark:bg-[#242a33] dark:border-[#374151]',
    listbox: 'dark:bg-[#242a33]',
    errorMessage: FIELD_LABELS.errorMessage,
  },
};
